import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Plus, 
  Calendar as CalendarIcon,
  Clock,
  Trash2,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Link,
  Unlink
} from "lucide-react";

const ORARI = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00"
];

export default function Appuntamenti() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [serviziPredefiniti, setServiziPredefiniti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    cliente_id: "",
    cliente_nome: "",
    data: format(new Date(), "yyyy-MM-dd"),
    ora: "09:00",
    servizi: [],
    note: ""
  });

  // Google Calendar state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  useEffect(() => {
    fetchData();
    checkGoogleStatus();
    
    // Check for callback params
    if (searchParams.get('google_connected') === 'true') {
      toast.success("Google Calendar connesso con successo!");
      checkGoogleStatus();
    }
    if (searchParams.get('error')) {
      toast.error("Errore nella connessione a Google Calendar");
    }
  }, []);

  useEffect(() => {
    fetchAppuntamenti();
  }, [selectedDate]);

  // Memoize appuntamenti ordinati per evitare sort ripetuti nel render
  const appuntamentiOrdinati = useMemo(() => {
    return [...appuntamenti].sort((a, b) => a.ora.localeCompare(b.ora));
  }, [appuntamenti]);

  const fetchData = async () => {
    try {
      const [clientiRes, serviziRes] = await Promise.all([
        axios.get(`${API}/clienti`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/servizi-predefiniti`)
      ]);
      setClienti(clientiRes.data);
      setServiziPredefiniti(serviziRes.data);
    } catch (error) {
      console.error("Errore caricamento dati iniziali:", error);
    }
  };

  const fetchAppuntamenti = async () => {
    setLoading(true);
    try {
      const dataStr = format(selectedDate, "yyyy-MM-dd");
      const response = await axios.get(`${API}/appuntamenti?data=${dataStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppuntamenti(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento appuntamenti");
    } finally {
      setLoading(false);
    }
  };

  const checkGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleConnected(response.data.connected);
      setGoogleEmail(response.data.google_email);
    } catch (error) {
      console.error("Errore verifica stato Google:", error);
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error("Errore nella connessione a Google Calendar");
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleConnected(false);
      setGoogleEmail(null);
      toast.success("Google Calendar disconnesso");
    } catch (error) {
      toast.error("Errore nella disconnessione");
    }
  };

  const syncToGoogle = async (appuntamentoId) => {
    if (!googleConnected) {
      toast.error("Connetti prima Google Calendar");
      return;
    }
    
    setSyncingId(appuntamentoId);
    try {
      await axios.post(`${API}/calendar/sync-appointment?appuntamento_id=${appuntamentoId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sincronizzato con Google Calendar!");
      fetchAppuntamenti();
    } catch (error) {
      toast.error("Errore nella sincronizzazione");
    } finally {
      setSyncingId(null);
    }
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    setFormData({
      ...formData,
      cliente_id: clienteId,
      cliente_nome: cliente ? `${cliente.nome} ${cliente.cognome}` : ""
    });
  };

  const handleServizioToggle = (servizio) => {
    const exists = formData.servizi.find(s => s.nome === servizio.nome);
    if (exists) {
      setFormData({
        ...formData,
        servizi: formData.servizi.filter(s => s.nome !== servizio.nome)
      });
    } else {
      setFormData({
        ...formData,
        servizi: [...formData.servizi, servizio]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.servizi.length === 0) {
      toast.error("Seleziona almeno un servizio");
      return;
    }
    try {
      const response = await axios.post(`${API}/appuntamenti`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appuntamento creato");
      
      // Auto-sync to Google Calendar if connected
      if (googleConnected && response.data.id) {
        try {
          await axios.post(`${API}/calendar/sync-appointment?appuntamento_id=${response.data.id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Sincronizzato con Google Calendar!");
        } catch (error) {
          // Sync Google Calendar fallita non blocca la creazione appuntamento
          console.warn('Sync Google Calendar fallita:', error?.message || error);
        }
      }
      
      setDialogOpen(false);
      resetForm();
      // Delay per evitare conflitti DOM
      setTimeout(() => {
        fetchAppuntamenti();
      }, 150);
    } catch (error) {
      toast.error("Errore nella creazione");
    }
  };

  const handleStatoChange = async (id, stato) => {
    try {
      await axios.put(`${API}/appuntamenti/${id}/stato?stato=${stato}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Stato aggiornato");
      // Delay per evitare conflitti DOM
      setTimeout(() => {
        fetchAppuntamenti();
      }, 100);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo appuntamento?")) return;
    try {
      // Get appointment to check for Google event
      const app = appuntamenti.find(a => a.id === id);
      if (app?.google_event_id && googleConnected) {
        try {
          await axios.delete(`${API}/calendar/event/${app.google_event_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          // Eliminazione evento Google Calendar fallita non blocca la cancellazione appuntamento
          console.warn('Eliminazione evento Google Calendar fallita:', error?.message || error);
        }
      }
      
      await axios.delete(`${API}/appuntamenti/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appuntamento eliminato");
      // Delay per evitare conflitti DOM
      setTimeout(() => {
        fetchAppuntamenti();
      }, 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: "",
      cliente_nome: "",
      data: format(selectedDate, "yyyy-MM-dd"),
      ora: "09:00",
      servizi: [],
      note: ""
    });
  };

  const totaleServizi = formData.servizi.reduce((sum, s) => sum + s.prezzo, 0);

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="appuntamenti">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Sidebar */}
        <Card className="border-0 shadow-lg" data-testid="calendar-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
              Calendario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={it}
              className="rounded-md"
            />
            
            {/* Google Calendar Connection */}
            <div className="mt-4 p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Google Calendar</span>
                {googleConnected ? (
                  <span className="badge badge-success text-xs">Connesso</span>
                ) : (
                  <span className="badge badge-warning text-xs">Non connesso</span>
                )}
              </div>
              
              {googleConnected ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 truncate">{googleEmail}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-red-500 border-red-200 hover:bg-red-50"
                    onClick={disconnectGoogleCalendar}
                    data-testid="disconnect-google"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnetti
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={connectGoogleCalendar}
                  data-testid="connect-google"
                >
                  <Link className="w-4 h-4 mr-2" />
                  Connetti Google Calendar
                </Button>
              )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
              else setFormData({ ...formData, data: format(selectedDate, "yyyy-MM-dd") });
            }}>
              <DialogTrigger asChild>
                <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700 btn-press" data-testid="new-appuntamento-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Appuntamento
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                    Nuovo Appuntamento
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Cliente</Label>
                    <Select value={formData.cliente_id} onValueChange={handleClienteChange}>
                      <SelectTrigger data-testid="select-cliente">
                        <SelectValue placeholder="Seleziona cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clienti.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.nome} {c.cognome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input
                        type="date"
                        value={formData.data}
                        onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                        required
                        data-testid="input-data"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ora</Label>
                      <Select value={formData.ora} onValueChange={(v) => setFormData({ ...formData, ora: v })}>
                        <SelectTrigger data-testid="select-ora">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORARI.map(ora => (
                            <SelectItem key={ora} value={ora}>{ora}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Servizi</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                      {serviziPredefiniti.map(servizio => (
                        <div
                          key={servizio.nome}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                            formData.servizi.find(s => s.nome === servizio.nome)
                              ? 'bg-blue-100 border-blue-300 border'
                              : 'bg-white border border-slate-200 hover:border-blue-200'
                          }`}
                          onClick={() => handleServizioToggle(servizio)}
                        >
                          <Checkbox
                            checked={!!formData.servizi.find(s => s.nome === servizio.nome)}
                            onCheckedChange={() => handleServizioToggle(servizio)}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{servizio.nome}</p>
                            <p className="text-xs text-slate-500">€{servizio.prezzo.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {formData.servizi.length > 0 && (
                      <div className="flex justify-between items-center mt-2 p-2 bg-blue-50 rounded-lg">
                        <span className="text-sm font-medium text-slate-600">Totale:</span>
                        <span className="text-lg font-bold text-blue-600">€{totaleServizi.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Textarea
                      value={formData.note}
                      onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                      placeholder="Note aggiuntive..."
                      data-testid="input-note"
                    />
                  </div>

                  {googleConnected && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
                      <Check className="w-4 h-4" />
                      L'appuntamento sarà sincronizzato automaticamente con Google Calendar
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    disabled={!formData.cliente_id || formData.servizi.length === 0}
                    data-testid="submit-appuntamento"
                  >
                    Conferma Appuntamento
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Appointments List */}
        <div className="lg:col-span-3 space-y-4">
          {/* Date Navigation */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h3 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}
                </h3>
                <Button variant="ghost" size="icon" onClick={() => navigateDate(1)}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Appointments */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : appuntamenti.length > 0 ? (
            <div className="space-y-3">
              {appuntamentiOrdinati.map((app, index) => (
                <Card 
                  key={app.id} 
                  className={`border-0 shadow-md card-hover animate-slideIn stagger-${Math.min(index + 1, 5)} ${
                    app.stato === 'cancellato' ? 'opacity-50' : ''
                  }`}
                  data-testid={`appuntamento-${app.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center text-white ${
                        app.stato === 'completato' ? 'bg-emerald-500' :
                        app.stato === 'cancellato' ? 'bg-slate-400' : 'bg-blue-600'
                      }`}>
                        <Clock className="w-4 h-4 mb-1" />
                        <span className="text-lg font-bold">{app.ora}</span>
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800 text-lg">{app.cliente_nome}</h4>
                        <p className="text-sm text-slate-500">
                          {app.servizi.map(s => s.nome).join(' + ')}
                        </p>
                        {app.note && (
                          <p className="text-xs text-slate-400 mt-1 italic">"{app.note}"</p>
                        )}
                      </div>

                      <div className="text-right">
                        <p className="text-xl font-bold text-blue-600 number-animate">€{app.totale.toFixed(2)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`badge ${
                            app.stato === 'completato' ? 'badge-success' :
                            app.stato === 'cancellato' ? 'badge-danger' : 'badge-info'
                          }`}>
                            {app.stato}
                          </span>
                          {app.google_event_id && (
                            <span className="badge badge-success text-xs">📅 Sync</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        {googleConnected && !app.google_event_id && app.stato !== 'cancellato' && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => syncToGoogle(app.id)}
                            disabled={syncingId === app.id}
                            title="Sincronizza con Google Calendar"
                            data-testid={`sync-${app.id}`}
                          >
                            <RefreshCw className={`w-5 h-5 ${syncingId === app.id ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                        
                        {app.stato === 'confermato' && (
                          <>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              onClick={() => handleStatoChange(app.id, 'completato')}
                              title="Completa"
                              data-testid={`complete-${app.id}`}
                            >
                              <Check className="w-5 h-5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 text-red-500 border-red-200 hover:bg-red-50"
                              onClick={() => handleStatoChange(app.id, 'cancellato')}
                              title="Cancella"
                              data-testid={`cancel-${app.id}`}
                            >
                              <X className="w-5 h-5" />
                            </Button>
                          </>
                        )}

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(app.id)}
                          data-testid={`delete-${app.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="empty-state py-12">
                  <div className="empty-state-icon">
                    <CalendarIcon className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 mb-4">Nessun appuntamento per questa data</p>
                  <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Appuntamento
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
